import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Instagram, Twitter } from "lucide-react";
import PageLayout from "../../components/layout/PageLayout";
import api from "../../lib/api";

export default function OwnerProfile() {
    const { slug } = useParams();
    const [o, setO] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/football/fantasy/owners/${slug}`).then(r => setO(r.data)).catch(e => setErr("Owner not found"));
    }, [slug]);

    if (err) return <PageLayout><div className="pt-40 text-center text-white/40" data-testid="owner-error">{err}</div></PageLayout>;
    if (!o) return <PageLayout><div className="pt-40 text-center text-white/40">Loading…</div></PageLayout>;

    return (
        <PageLayout>
            <section className="relative min-h-[70vh] flex items-end pb-14 pt-24 overflow-hidden" data-testid="owner-profile-hero">
                <img src={o.photo} alt={o.name} className="absolute inset-0 w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
                    <div className="section-label mb-4">Fantasy Owner</div>
                    <div className="flex flex-wrap justify-between items-end gap-4">
                        <div>
                            <h1 className="font-display text-5xl md:text-8xl text-white leading-[0.9] tracking-tight" data-testid="owner-team-name">
                                {o.team_name.toUpperCase()}
                            </h1>
                            <div className="mt-4 flex flex-wrap items-center gap-3 text-white/80 uppercase tracking-widest text-xs">
                                <span>{o.name}</span><span>·</span><span>{o.city}</span>
                                <span>·</span><span className="text-[#d4af37]">Rank #{o.league_rank}</span>
                            </div>
                        </div>
                        {o.espn_team_url && (
                            <a href={o.espn_team_url} target="_blank" rel="noreferrer" className="btn-outline" data-testid="owner-espn-btn">
                                ESPN Team <ExternalLink size={14} />
                            </a>
                        )}
                    </div>
                </div>
            </section>

            <section className="py-12">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-8">
                        <div>
                            <div className="section-label mb-4">Bio</div>
                            <p className="text-white/80 text-lg leading-relaxed">{o.bio}</p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="owner-stats">
                            {[
                                { l: "W-L", v: `${o.record_wins}-${o.record_losses}` },
                                { l: "PF", v: o.points_for },
                                { l: "PA", v: o.points_against },
                                { l: "Streak", v: o.streak || "—" },
                                { l: "Fav NFL", v: o.favorite_team || "—" },
                                { l: "Strategy", v: o.draft_strategy || "—" },
                                { l: "Odds", v: o.championship_odds || "—" },
                                { l: "DB Power", v: `#${o.power_ranking}` },
                            ].map(x => (
                                <div key={x.l} className="surface p-5">
                                    <div className="text-white/40 text-[10px] uppercase tracking-[0.24em]">{x.l}</div>
                                    <div className="font-display text-2xl text-[#d4af37] mt-2 leading-none">{x.v}</div>
                                </div>
                            ))}
                        </div>

                        {o.trash_talk && (
                            <div className="border-l-2 border-[#d4af37] pl-5">
                                <div className="section-label mb-2">Trash Talk</div>
                                <p className="font-display text-2xl italic text-white">"{o.trash_talk}"</p>
                            </div>
                        )}
                    </div>

                    <aside className="lg:col-span-4 space-y-4">
                        <div className="surface p-6">
                            <div className="section-label mb-4">This Week</div>
                            <div className="text-white/40 text-[10px] uppercase tracking-widest">Matchup</div>
                            <div className="font-display text-2xl text-white mt-1">vs {o.weekly_opponent}</div>
                        </div>
                        {(o.social?.instagram || o.social?.x) && (
                            <div className="surface p-6">
                                <div className="section-label mb-4">Social</div>
                                <div className="space-y-3">
                                    {o.social?.instagram && <a href={`https://instagram.com/${o.social.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/80 hover:text-[#d4af37]"><Instagram size={16}/> {o.social.instagram}</a>}
                                    {o.social?.x && <a href="#" className="flex items-center gap-3 text-white/80 hover:text-[#d4af37]"><Twitter size={16}/> {o.social.x}</a>}
                                </div>
                            </div>
                        )}
                        <Link to="/football/fantasy" className="btn-outline w-full justify-center" data-testid="owner-back">Back to Clubhouse</Link>
                    </aside>
                </div>
            </section>
        </PageLayout>
    );
}
